'use client';

import { ArrowRight, X } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useState, useRef } from 'react';
import { blogDrawerStorage } from '@/lib/blog-drawer-storage';
import { useAnalytics } from '@/services/frontendAnalyticsService';

interface BlogTrialBannerDesktopProps {
  postSlug: string;
  isAuthenticated: boolean;
}

export function BlogTrialBannerDesktop({ postSlug, isAuthenticated }: BlogTrialBannerDesktopProps) {
  const analytics = useAnalytics();
  const router = useRouter();
  const [showBanner, setShowBanner] = useState(false);
  const hasShownRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated || blogDrawerStorage.isCooldownActive()) return;

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const clientHeight = window.innerHeight;

      const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;

      if (scrollPercentage >= 25 && !hasShownRef.current) {
        setShowBanner(true);
        hasShownRef.current = true;
        analytics.blog.drawer.shown({ postSlug, scrollDepth: scrollPercentage, device: 'desktop' });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAuthenticated, postSlug, analytics]);

  const handleClose = () => {
    setShowBanner(false);
    blogDrawerStorage.setDismissed();
    analytics.blog.drawer.closed({ postSlug, device: 'desktop' });
  };

  const handleCTAClick = () => {
    analytics.blog.drawer.ctaClicked({ postSlug, device: 'desktop' });
    analytics.blog.featuresCTAClicked({
      postSlug,
      ctaType: 'drawer',
      ctaLabel: 'Mulai Trial Gratis',
    });
    blogDrawerStorage.setDismissed();
    router.push('/features');
  };

  if (isAuthenticated || !showBanner) return null;

  return (
    <div className="hidden md:block fixed bottom-0 left-0 right-0 z-40 bg-white border-t-2 border-black shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-black uppercase tracking-tight">
            Coba Fitur Professional Gratis
          </h3>
          <p className="text-sm text-neutral-600 line-clamp-1">
            Akses 24 jam konsolidasi, AI research, dan pemetaan relasi — tanpa kewajiban berlangganan.
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={handleCTAClick}
            className="bg-neutral-900 text-white px-6 py-2.5 font-bold text-sm border-2 border-black hover:bg-neutral-800 transition-colors flex items-center gap-2"
          >
            Mulai Trial Gratis
            <ArrowRight className="w-4 h-4" />
          </button>

          <button
            onClick={handleClose}
            className="p-2 rounded-sm opacity-70 hover:opacity-100 hover:bg-neutral-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
